/**
 * <email-popup> — configurable email-capture popup.
 *
 * Handles: the engagement-gated display trigger + suppression rules, the scratch-card
 * interaction, the reveal countdown, customer creation (POST to /contact, mirroring
 * assets/newsletter.js), and Mixpanel funnel tracking.
 *
 * There is no success stage. Submitting applies the discount to the cart in the background
 * (see applyDiscountInBackground) and confirms with a notification bar that opens in the
 * announcement strip at the top of the page and closes itself — no code to copy by hand,
 * nowhere to navigate, nothing to dismiss.
 *
 * Funnel (all events carry { variant } plus window.__rumContext):
 *   Email Capture Shown -> Scratch Started -> Scratch Completed
 *   -> Email Submitted -> Success -> Discount Auto Applied
 * Plus: Suppressed, Dismissed, Expired, Code Copied.
 */

/**
 * Behaviour defaults.
 *
 * Every one of these is overridden by the matching setting in the "Triggers" and
 * "Suppression rules" groups in the theme editor (delivered via the JSON config blob the
 * section emits). They're the fallback for a missing or unparseable config only — edit the
 * settings, not these.
 *
 * The trigger model: two gates, then a backstop. Nothing opens until the visitor has shown
 * they intend to stay (gate 1: pageviews, scroll or dwell), and then only on an exit signal
 * (gate 2: exit intent or scroll-up). A bare timer, which is what this used to be, fires at
 * people still deciding whether to stay at all.
 *
 * The backstop (trigger_backstop_dwell) sits outside both gates: enough *active* dwell —
 * visible and focused — opens the popup on its own, because by then waiting for an exit
 * signal that may never come just leaves the offer on the table. See fire().
 *
 * A gate set to 0 in the editor is switched off. All of them off means the popup only ever
 * opens via the backstop; that and the backstop off means it never opens at all.
 */
const DEFAULTS = {
  // Suppression
  frequency_days: 3,
  suppress_logged_in: true,
  suppress_cart_page: true,
  suppress_cart_not_empty: false,
  suppress_discount_in_url: true,
  suppress_utm_mediums: 'email,sms',
  defer_while_overlay_open: true,
  // Triggers
  trigger_min_delay: 5,
  trigger_engage_pageviews: 2,
  trigger_engage_scroll: 45,
  trigger_engage_dwell: 20,
  trigger_exit_intent: true,
  trigger_scroll_up_px: 250,
  trigger_backstop_dwell: 45,
  // Variant A
  countdown_seconds: 120,
  scratch_threshold: 30,
};

// How often to re-check whether a blocking overlay has closed. Not a setting: it's an
// implementation detail of waiting, not a thing to tune.
const OVERLAY_RECHECK_MS = 400;

// How long the confirmation bar stays open before closing itself. Measured from insertion,
// so the ~550ms entrance (slide + copy fade) comes out of it — this leaves roughly 2.5s of
// settled, readable bar. Long enough to read the line and reach for the code; short enough
// that it doesn't sit at the top of the page outstaying the moment it's confirming.
// Any interaction with the code chip restarts it (see restartBarTimer).
const BAR_DWELL_MS = 3100;

/**
 * Dev override. The trigger stack is deliberately hard to fire, which makes the popup
 * awkward to look at while working on it, so:
 *
 *   ?email_capture=force   open immediately, ignoring every gate and suppression rule
 *   ?email_capture=reset   clear this browser's claimed / last-shown / session state
 *   ?email_capture=debug   log each trigger and suppression decision to the console
 *
 * `force` and `reset` can be combined: ?email_capture=reset,force
 *
 * Safe to leave in: it only ever loosens behaviour for whoever types it into their own
 * address bar, and it cannot show the popup in a territory the Liquid gate has blocked —
 * in those markets this file never loads.
 */
const DEV_PARAM = 'email_capture';

class EmailPopup extends HTMLElement {
  constructor() {
    super();

    this.STORAGE_CLAIMED = 'emailCapture:claimed';
    this.STORAGE_LAST_SHOWN = 'emailCapture:lastShown';
    // Pageviews this session (sessionStorage) — gate 1 of the trigger stack.
    this.STORAGE_VIEWS = 'emailCapture:views';
    // Sticky "this session arrived from a campaign" flag. Needed because the utm_*
    // params are gone by the second click, which would make the rule decorative.
    this.STORAGE_CAMPAIGN = 'emailCapture:campaignTraffic';
    // One suppression event per session, not one per pageview.
    this.STORAGE_SUPPRESS_LOGGED = 'emailCapture:suppressLogged';
    // Fragment tacked onto the form action so the native-fallback submit's
    // redirect is attributable to this popup (survives Shopify's /contact
    // redirect). Used to re-open on the success stage after that reload.
    this.SUCCESS_HASH = 'email-capture-success';

    this.variant = this.dataset.variant || 'a';
    this.designMode =
      this.dataset.designMode === 'true' ||
      (window.Shopify && window.Shopify.designMode) ||
      false;

    this.opened = false;
    this.closing = false;
    this.scratchStarted = false;
    this.scratchCompleted = false;
    this.countdownTimer = null;

    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.readConfig();
    this.readDevFlags();

    // Cache DOM references.
    this.dialog = this.querySelector('.email-capture__dialog');
    // .js-ec-countdown, not .js-countdown — see the comment in
    // snippets/email-capture-variant-a.liquid: assets/countdown.js claims the latter globally.
    this.countdownEl = this.querySelector('.js-ec-countdown');
    this.scratchWrap = this.querySelector('.js-scratch');
    this.canvas = this.querySelector('.js-scratch-canvas');
    this.scratchLabel = this.querySelector('.js-scratch-label');
    this.headingEl = this.querySelector('.js-ec-heading');
    this.form = this.querySelector('.js-email-capture-form');
    this.emailInput = this.querySelector('.js-email-capture-email');
    this.submitBtn = this.form ? this.form.querySelector('[type="submit"]') : null;

    this.bindEvents();
    // Keep one random side image; the popup is still hidden here so the dropped
    // (lazy) images never load.
    this.pickRandomMedia();

    // Tag the form action so the native submit's redirect is attributable to us (see
    // submitNative / shouldRestoreSuccess): the #email-capture-success fragment rides the
    // customer_posted redirect and tells us, on the way back, that this submit was ours.
    if (this.form && !this.designMode) {
      const action = (this.form.getAttribute('action') || '/contact').split('#')[0];
      this.form.setAttribute('action', action + '#' + this.SUCCESS_HASH);
    }

    // Returning from the native submit: confirm on the page and stop. Notably does not
    // open the popup or arm any trigger — they've already subscribed.
    if (this.shouldRestoreSuccess()) {
      this.completeSuccess();
      return;
    }

    // Defer the whole startup path until the page is actually on screen. A prerendered
    // load or a tab opened in the background must not count a pageview, accumulate
    // dwell, or emit a suppression event — and must not be written off either, since
    // the visitor may well look at it in a minute.
    this.whenVisible(() => this.start());
  }

  start() {
    this.countPageview();

    if (this.dev.force) {
      this.log('force: bypassing gates and suppression');
      this.triggerReason = 'dev_force';
      this.open();
      return;
    }

    const eligibility = this.evaluateEligibility();
    if (!eligibility.show) {
      this.log('suppressed:', eligibility.reason);
      this.trackSuppressed(eligibility.reason);
      return;
    }

    this.armTriggers();
  }

  disconnectedCallback() {
    window.clearTimeout(this.revealTimer);
    window.clearTimeout(this.twTimer);
    this.stopCountdown();
    this.stopSlideshow();
    this.teardownMaterial();
    this.disarmTriggers();
    this.dismissConfirmationBar(true);
    if (this.onVisibilityDeferred) {
      document.removeEventListener('visibilitychange', this.onVisibilityDeferred);
      this.onVisibilityDeferred = null;
    }
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* ---------- page visibility ---------- */

  // Run fn now if the page is visible, otherwise on the first time it becomes visible.
  whenVisible(fn) {
    if (document.visibilityState !== 'hidden') {
      fn();
      return;
    }
    this.onVisibilityDeferred = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', this.onVisibilityDeferred);
      this.onVisibilityDeferred = null;
      fn();
    };
    document.addEventListener('visibilitychange', this.onVisibilityDeferred);
  }

  // Visible AND focused. Dwell only accrues while both hold, so time spent in a
  // background tab or another window never counts toward engagement.
  isActive() {
    return document.visibilityState !== 'hidden' && document.hasFocus();
  }

  isFinePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
  }

  /* ---------- session counters ---------- */

  countPageview() {
    if (this.designMode) return;
    try {
      this.views =
        parseInt(window.sessionStorage.getItem(this.STORAGE_VIEWS) || '0', 10) + 1;
      window.sessionStorage.setItem(this.STORAGE_VIEWS, String(this.views));
    } catch (e) {
      this.views = 1; /* storage blocked — treat as a first view */
    }
  }

  /* ---------- storefront context ---------- */

  // Parse the JSON blob the section emits (customer, cart_count, page_type, country).
  // Never throws: a malformed or missing blob leaves an empty config, and every cfg() caller
  // passes a fallback, so the popup degrades to "no storefront context" rather than breaking.
  readConfig() {
    this.config = {};
    const el = this.querySelector('.js-email-capture-config');
    if (!el) return;
    try {
      this.config = JSON.parse(el.textContent) || {};
    } catch (e) {
      /* malformed blob — leave config empty */
    }
  }

  // Settings win; DEFAULTS above are the fallback for a key that isn't in the config at all.
  // An empty string is a real value, not a missing one — clearing suppress_utm_mediums in the
  // editor means "switch this rule off", so it must not fall back to the default list.
  cfg(key, fallback) {
    const v = this.config ? this.config[key] : undefined;
    if (v === undefined || v === null) {
      return fallback !== undefined ? fallback : DEFAULTS[key];
    }
    return v;
  }

  // Numeric setting, coerced. 0 is meaningful (it switches a gate off), so it must survive.
  num(key) {
    const v = parseInt(this.cfg(key), 10);
    return isNaN(v) ? DEFAULTS[key] : v;
  }

  bool(key) {
    return !!this.cfg(key);
  }

  /* ---------- dev override ---------- */

  readDevFlags() {
    this.dev = { force: false, reset: false, debug: false };
    let raw = '';
    try {
      raw = (new URLSearchParams(window.location.search).get(DEV_PARAM) || '').toLowerCase();
    } catch (e) {
      return;
    }
    if (!raw) return;
    raw.split(',').forEach((flag) => {
      const key = flag.trim();
      if (key in this.dev) this.dev[key] = true;
    });

    if (this.dev.reset) {
      try {
        window.localStorage.removeItem(this.STORAGE_CLAIMED);
        window.localStorage.removeItem(this.STORAGE_LAST_SHOWN);
        window.sessionStorage.removeItem(this.STORAGE_VIEWS);
        window.sessionStorage.removeItem(this.STORAGE_CAMPAIGN);
        window.sessionStorage.removeItem(this.STORAGE_SUPPRESS_LOGGED);
      } catch (e) {
        /* ignore */
      }
      this.log('reset: cleared claimed / last-shown / session state');
    }
  }

  log() {
    if (!this.dev || !this.dev.debug) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift('[email-capture]');
    // eslint-disable-next-line no-console
    if (window.console && window.console.log) window.console.log.apply(window.console, args);
  }

  /* ---------- eligibility ---------- */

  // Returns { show, reason }. The reason is what makes the frequency cap and the
  // suppression rules measurable — without it you can't tell a popup nobody saw from
  // a popup nobody converted on. The territory veto isn't here: that's decided in
  // Liquid, so this element never reaches the page at all in a blocked market.
  evaluateEligibility() {
    // In the theme editor, preview only when the merchant has enabled it, and skip
    // every other rule so the preview isn't suppressed by the merchant's own session.
    if (this.designMode) {
      return this.dataset.enabled === 'true'
        ? { show: true }
        : { show: false, reason: 'disabled' };
    }

    if (navigator.webdriver) return { show: false, reason: 'automation' };

    if (this.bool('suppress_logged_in') && this.cfg('customer', false)) {
      return { show: false, reason: 'logged_in' };
    }
    if (this.bool('suppress_cart_page') && this.cfg('page_type', '') === 'cart') {
      return { show: false, reason: 'cart_page' };
    }
    if (this.bool('suppress_cart_not_empty') && this.cfg('cart_count', 0) > 0) {
      return { show: false, reason: 'cart_not_empty' };
    }
    if (this.bool('suppress_discount_in_url') && this.hasDiscountInPlay()) {
      return { show: false, reason: 'discount_url' };
    }
    if (this.isCampaignTraffic()) return { show: false, reason: 'campaign_traffic' };

    try {
      if (window.localStorage.getItem(this.STORAGE_CLAIMED)) {
        return { show: false, reason: 'claimed' };
      }
      const days = this.num('frequency_days');
      const last = parseInt(
        window.localStorage.getItem(this.STORAGE_LAST_SHOWN) || '0',
        10
      );
      if (days > 0 && last && Date.now() - last < days * 86400000) {
        return { show: false, reason: 'frequency_cap' };
      }
    } catch (e) {
      /* storage blocked — fail open and show once */
    }

    return { show: true };
  }

  // A discount is already in play, so don't offer a competing one.
  hasDiscountInPlay() {
    try {
      if (new URLSearchParams(window.location.search).get('discount')) return true;
    } catch (e) {
      /* ignore */
    }
    // Shopify sets this when a /discount/<code> link has been applied, so it survives
    // the visitor navigating on from the page they landed on.
    return /(^|;\s*)discount_code=/.test(document.cookie || '');
  }

  // Arrived from an email or SMS campaign — they were already given whatever offer that
  // campaign carried. Sticky for the session, because utm_* is gone after the first click.
  isCampaignTraffic() {
    try {
      if (window.sessionStorage.getItem(this.STORAGE_CAMPAIGN)) return true;
    } catch (e) {
      /* ignore */
    }
    let medium = '';
    try {
      medium = (
        new URLSearchParams(window.location.search).get('utm_medium') || ''
      ).toLowerCase();
    } catch (e) {
      /* ignore */
    }
    const mediums = String(this.cfg('suppress_utm_mediums'))
      .toLowerCase()
      .split(',')
      .map(function (m) {
        return m.trim();
      })
      .filter(Boolean);
    if (medium && mediums.indexOf(medium) !== -1) {
      try {
        window.sessionStorage.setItem(this.STORAGE_CAMPAIGN, '1');
      } catch (e) {
        /* ignore */
      }
      return true;
    }
    return false;
  }

  // Once per session, not once per pageview: a popup suppressed on most pageviews would
  // otherwise emit on most pageviews, which is a real Mixpanel bill for no extra signal.
  trackSuppressed(reason) {
    if (!reason || this.designMode) return;
    try {
      if (window.sessionStorage.getItem(this.STORAGE_SUPPRESS_LOGGED)) return;
      window.sessionStorage.setItem(this.STORAGE_SUPPRESS_LOGGED, reason);
    } catch (e) {
      /* storage blocked — at worst this is once per pageview */
    }
    this.track('Email Capture Suppressed', { reason: reason });
  }

  // True when this page load is the redirect back from a native-fallback
  // submit: Shopify appends ?customer_posted=true and our #email-capture-success
  // fragment rides along through the redirect.
  shouldRestoreSuccess() {
    if (this.designMode) return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return (
        params.get('customer_posted') === 'true' &&
        window.location.hash.replace('#', '') === this.SUCCESS_HASH
      );
    } catch (e) {
      return false;
    }
  }

  /* ---------- trigger stack ---------- */

  armTriggers() {
    // The editor gets no gates — a merchant configuring the popup needs to see it now.
    if (this.designMode) {
      this.open();
      return;
    }

    this.armedAt = Date.now();
    this.fired = false;
    this.engaged = false;
    this.dwellMs = 0;
    this.deepestScrollY = window.scrollY || 0;

    this.minDelayMs = this.num('trigger_min_delay') * 1000;
    this.engagePageviews = this.num('trigger_engage_pageviews');
    this.engageScrollPct = this.num('trigger_engage_scroll');
    this.engageDwellMs = this.num('trigger_engage_dwell') * 1000;
    this.backstopDwellMs = this.num('trigger_backstop_dwell') * 1000;
    this.scrollUpPx = this.num('trigger_scroll_up_px');

    this.log('armed', {
      views: this.views,
      min_delay_s: this.minDelayMs / 1000,
      engage_pageviews: this.engagePageviews,
      engage_scroll_pct: this.engageScrollPct,
      engage_dwell_s: this.engageDwellMs / 1000,
      backstop_dwell_s: this.backstopDwellMs / 1000,
      exit_intent: this.bool('trigger_exit_intent'),
      scroll_up_px: this.scrollUpPx,
    });

    // Gate 1 may already be satisfied by the session's history, before any interaction.
    if (this.engagePageviews > 0 && (this.views || 1) >= this.engagePageviews) {
      this.setEngaged('pageviews');
    }

    this.startDwellClock();
    this.bindScrollTriggers();
    this.bindExitIntent();
  }

  disarmTriggers() {
    window.clearInterval(this.dwellTimer);
    window.clearInterval(this.overlayPoll);
    window.clearTimeout(this.resizeTimer);
    this.dwellTimer = null;
    this.overlayPoll = null;

    if (this.onActivityChange) {
      document.removeEventListener('visibilitychange', this.onActivityChange);
      window.removeEventListener('blur', this.onActivityChange);
      window.removeEventListener('focus', this.onActivityChange);
      this.onActivityChange = null;
    }
    if (this.offLenisScroll) {
      this.offLenisScroll();
      this.offLenisScroll = null;
    }
    if (this.onWindowScroll) {
      window.removeEventListener('scroll', this.onWindowScroll);
      this.onWindowScroll = null;
    }
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      this.onResize = null;
    }
    if (this.onMouseOut) {
      document.removeEventListener('mouseout', this.onMouseOut);
      this.onMouseOut = null;
    }
  }

  // Gate 1 (20s) and gate 3 (45s) both run off accumulated *active* time. One 1s
  // interval, no layout reads. The interval is also ticked on every activity change so
  // the accumulator closes its open span the moment the tab is hidden or blurred.
  startDwellClock() {
    let activeSince = this.isActive() ? Date.now() : 0;

    const tick = () => {
      if (activeSince) {
        this.dwellMs += Date.now() - activeSince;
        activeSince = 0;
      }
      if (this.isActive()) activeSince = Date.now();

      if (this.engageDwellMs > 0 && this.dwellMs >= this.engageDwellMs) {
        this.setEngaged('dwell');
      }
      if (this.backstopDwellMs > 0 && this.dwellMs >= this.backstopDwellMs) {
        this.fire('dwell_backstop');
      }
    };

    this.dwellTimer = window.setInterval(tick, 1000);
    this.onActivityChange = tick;
    document.addEventListener('visibilitychange', this.onActivityChange);
    window.addEventListener('blur', this.onActivityChange);
    window.addEventListener('focus', this.onActivityChange);
  }

  bindScrollTriggers() {
    // Lenis hands us normalized progress and reads no layout, so prefer it. Note it only
    // initialises above 991px (see assets/lenis.js), so touch always takes the fallback.
    if (window.lenis && typeof window.lenis.on === 'function') {
      this.offLenisScroll = window.lenis.on('scroll', (inst) => {
        const pct = (inst && typeof inst.progress === 'number' ? inst.progress : 0) * 100;
        this.onScrollSignal(pct, window.scrollY || 0);
      });
      return;
    }

    // Fallback: ONE cached scalar, refreshed on debounced resize, listener passive and
    // removed the instant it fires. This is not the pattern assets/aos.js warns against —
    // that prohibition is on per-element getBoundingClientRect() in a scroll+mutation
    // loop, not on a single cached document height.
    this.cacheScrollRange();
    this.onResize = () => {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.cacheScrollRange(), 200);
    };
    this.onWindowScroll = () => {
      const y = window.scrollY || 0;
      this.onScrollSignal(this.scrollRange ? (y / this.scrollRange) * 100 : 0, y);
    };
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
  }

  cacheScrollRange() {
    this.scrollRange = Math.max(
      1,
      document.documentElement.scrollHeight - window.innerHeight
    );
  }

  onScrollSignal(pct, y) {
    if (this.engageScrollPct > 0 && pct >= this.engageScrollPct) {
      this.setEngaged('scroll');
    }

    if (y > this.deepestScrollY) this.deepestScrollY = y;
    // Touch exit proxy — only after they've gone deep, so it can't fire on the
    // small upward corrections people make while reading.
    if (
      this.scrollUpPx > 0 &&
      !this.isFinePointer() &&
      this.engaged &&
      this.deepestScrollY - y >= this.scrollUpPx
    ) {
      this.fire('scroll_up_intent');
    }
  }

  setEngaged(via) {
    if (this.engaged) return;
    this.engaged = true;
    this.log('engaged via', via);
  }

  bindExitIntent() {
    // (pointer: fine) is the right test for "has a cursor that can leave the viewport",
    // rather than a width breakpoint that mislabels small laptops and large tablets.
    if (!this.bool('trigger_exit_intent')) return;
    if (!this.isFinePointer()) return;
    this.onMouseOut = (e) => {
      // relatedTarget null means the pointer left the document, not just an element.
      if (e.relatedTarget || e.clientY > 0) return;
      this.fire('exit_intent');
    };
    document.addEventListener('mouseout', this.onMouseOut);
  }

  fire(reason) {
    if (this.opened || this.fired) return;
    // The backstop is deliberately exempt from gate 1. It is the trigger that fires when
    // nothing else did, and the dwell it counts is *active* dwell — visible and focused — so
    // by the time it fires it is a stronger signal of intent than any engagement gate it
    // would otherwise be waiting behind. Without the exemption, zeroing all three engagement
    // gates (which the settings explicitly allow, and the defaults above describe) left the
    // popup unable to open at all: the backstop fired every tick and was discarded here.
    if (!this.engaged && reason !== 'dwell_backstop') {
      this.log('ignored', reason, '— not engaged yet');
      return;
    }
    if (Date.now() - this.armedAt < this.minDelayMs) {
      this.log('ignored', reason, '— inside the minimum delay');
      return;
    }

    // Hold rather than drop: stacking a modal on top of the cart drawer or the mobile
    // menu is the kind of thing that shows up later as unexplained bounce.
    if (this.bool('defer_while_overlay_open') && this.overlayOpen()) {
      this.log('holding', reason, '— another overlay is open');
      this.holdForOverlay(reason);
      return;
    }

    this.log('opening via', reason);

    this.fired = true;
    this.triggerReason = reason;
    this.disarmTriggers();
    this.open();
  }

  // Every overlay in this theme locks scroll the same way — window.lenis.stop() on
  // desktop, body.style.overflow on touch (where Lenis isn't initialised). Reading that
  // catches the cart drawer, contact drawer, megamenu, mobile menu, predictive search,
  // the mobile facet drawer and the PDP lightbox, including any added later — which
  // enumerating each one's own class would not.
  overlayOpen() {
    if (window.lenis && window.lenis.isStopped) return true;
    return document.body.style.overflow === 'hidden';
  }

  holdForOverlay(reason) {
    if (this.overlayPoll) return;
    this.overlayPoll = window.setInterval(() => {
      if (this.overlayOpen()) return;
      window.clearInterval(this.overlayPoll);
      this.overlayPoll = null;
      this.fire(reason);
    }, OVERLAY_RECHECK_MS);
  }

  /* ---------- open / close ---------- */

  open() {
    if (this.opened) return;
    this.opened = true;

    this.hidden = false;
    this.classList.add('display');
    // Force a reflow before adding the transition class.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('active'));
    });

    this.lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    if (!this.designMode) {
      try {
        window.localStorage.setItem(this.STORAGE_LAST_SHOWN, String(Date.now()));
      } catch (e) {
        /* ignore */
      }
    }

    this.setStage('scratch');
    // Canvas needs its final layout size before we draw the cover.
    requestAnimationFrame(() => this.setupScratch());
    // Interactive foil lighting (pointer hotspot + card tilt). Purely additive;
    // no-op under reduced motion.
    this.setupMaterial();
    this.startSlideshow();

    // Which gate opened it — so the trigger stack can be judged per signal rather than
    // in aggregate. Undefined in the theme editor, which never arms triggers.
    this.track('Email Capture Shown', { trigger: this.triggerReason || 'design_mode' });
  }

  // Open directly on the success stage after a native-fallback reload.
  // Returning from the /contact submit. Deliberately does NOT open the popup: the
  // visitor already gave us the email, and holding them in a modal to copy a code by hand
  // was the step that made this feel long. The discount is applied to the cart in the
  // background and confirmed with a notification bar in the announcement strip.
  completeSuccess() {
    try {
      window.localStorage.setItem(this.STORAGE_CLAIMED, '1');
    } catch (e) {
      /* ignore */
    }
    // Strip the ?customer_posted / #success markers so a manual refresh doesn't re-run this.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch (e) {
      /* ignore */
    }

    this.applyDiscountInBackground();
    this.showConfirmationBar();
    this.track('Email Capture Success');
  }

  // Put the discount on the cart without making the visitor do anything. /discount/<code>
  // sets the cart's discount cookie server-side and then redirects; fetching it has the
  // same effect as clicking it, so by the time the success panel is on screen the code is
  // already applied and "copy it, then paste it at checkout" stops being a step.
  //
  // The redirect target is /cart.js rather than a page: the cookie is set either way, and
  // this pulls a few hundred bytes of JSON instead of a whole rendered template.
  //
  // Belt and braces — the CTA still carries the permalink (see discountUrl), so if this
  // request fails the click path applies the discount exactly as it did before.
  applyDiscountInBackground() {
    if (this.designMode || this.discountRequested) return;
    const code = this.dataset.discountCode;
    if (!code || typeof window.fetch !== 'function') return;

    this.discountRequested = true;
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url =
      root +
      'discount/' +
      encodeURIComponent(code) +
      '?redirect=' +
      encodeURIComponent(root + 'cart.js');

    window
      .fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res || !res.ok) throw new Error('discount route returned ' + (res && res.status));
        this.setAttribute('data-discount-applied', 'true');
        this.track('Email Capture Discount Auto Applied');
        this.log('discount applied to cart in the background');
      })
      .catch((err) => {
        this.log('background apply failed — the CTA link will still apply it', err);
      });
  }

  close() {
    if (!this.opened || this.closing) return;
    this.closing = true;

    const stage = this.getAttribute('data-stage');
    if (stage !== 'success') {
      this.track('Email Capture Dismissed', { stage: stage });
    }

    this.classList.add('closing');
    this.stopCountdown();
    this.stopSlideshow();
    window.clearTimeout(this.revealTimer);
    window.clearTimeout(this.twTimer);

    window.setTimeout(() => {
      this.classList.remove('active', 'closing', 'display');
      this.hidden = true;
      this.opened = false;
      this.closing = false;
      this.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
    }, 300);
  }

  onKeydown(e) {
    if (e.key === 'Escape') this.close();
  }

  lockScroll() {
    if (window.lenis) window.lenis.stop();
    else document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (window.lenis) window.lenis.start();
    else document.body.style.overflow = '';
  }

  /* ---------- stage machine ---------- */

  setStage(stage) {
    // Drives which stage panel + the countdown are visible (see CSS [data-stage]).
    this.setAttribute('data-stage', stage);
  }

  /* ---------- events ---------- */

  bindEvents() {
    this.querySelectorAll('.js-email-capture-close').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    if (this.emailInput) {
      // Constraint validation fires this when a submit is attempted against an empty or
      // malformed address. preventDefault() suppresses the browser's validation popup — the
      // one piece of this flow that cannot be styled — and markInvalid() puts the same
      // message where it can be: in the field's own placeholder.
      this.emailInput.addEventListener('invalid', (e) => {
        e.preventDefault();
        this.markInvalid();
        this.shakeSubmit();
      });

      // Any edit clears it. Not on focus: someone who tabs in, reads the message and tabs
      // out again should not have it vanish before they have acted on it.
      this.emailInput.addEventListener('input', () => this.clearInvalid());
    }

  }

  // Stand in for the browser's validation popup, which is unstyleable: swap the placeholder
  // for the error copy and turn the field red. The original placeholder is captured on first
  // use rather than read from a second data attribute — one source for it, and it survives
  // anything that rewrites the markup before the first failed submit.
  //
  // aria-invalid rather than a visually hidden live region: the field is focused by the
  // browser as part of validating it, and a focused input's placeholder is announced, so the
  // message reaches a screen reader through the same change that shows it on screen.
  markInvalid() {
    const input = this.emailInput;
    if (!input) return;

    if (this.defaultPlaceholder == null) this.defaultPlaceholder = input.placeholder;

    const message = input.dataset.invalidPlaceholder;
    if (message) input.placeholder = message;
    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
  }

  clearInvalid() {
    const input = this.emailInput;
    if (!input || !input.classList.contains('is-invalid')) return;

    if (this.defaultPlaceholder != null) input.placeholder = this.defaultPlaceholder;
    input.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
  }

  // The claim button used to sit disabled and greyed out until the field had a value, which
  // read as broken rather than as "not yet" — it is the primary action of the whole popup and
  // it spent most of its life looking switched off. It now keeps its solid appearance and an
  // invalid submit shakes it instead.
  //
  // The disabled state still exists, but only while actually submitting: onSubmit() sets it
  // alongside .is-loading, which is what the grey styling is now for.
  shakeSubmit() {
    if (!this.submitBtn || this.prefersReducedMotion()) return;

    // Remove, force a reflow, re-add: without the reflow the class is still present from the
    // previous attempt and the animation does not restart on a second failed submit.
    this.submitBtn.classList.remove('is-invalid');
    void this.submitBtn.offsetWidth;
    this.submitBtn.classList.add('is-invalid');
  }

  // Randomise the desktop side image: keep one of the rendered candidates and
  // remove the rest so a repeat visitor doesn't see the same image every time.
  // Runs while the popup is display:none, so the dropped lazy images never load.
  pickRandomMedia() {
    const imgs = this.querySelectorAll('.js-email-capture-media-img');
    if (imgs.length < 2) return;
    const keep = Math.floor(Math.random() * imgs.length);
    imgs.forEach((img, i) => {
      if (i !== keep) img.remove();
    });
  }

  /* ---------- rotating print close-ups ---------- */

  // Crossfade through all .email-capture__scratch-slide elements, however many
  // there are (add/remove <img>s in the markup, no CSS changes needed).
  startSlideshow() {
    const SLIDE_MS = 2500;
    const slides = this.querySelectorAll('.email-capture__scratch-slide');
    if (!slides.length) return;

    let i = 0;
    slides[0].classList.add('is-active');
    if (slides.length < 2) return;

    this.slideTimer = window.setInterval(() => {
      slides[i].classList.remove('is-active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('is-active');
    }, SLIDE_MS);
  }

  stopSlideshow() {
    window.clearInterval(this.slideTimer);
    this.slideTimer = null;
  }

  /* ---------- scratch card ---------- */

  setupScratch() {
    if (!this.canvas || !this.scratchWrap) return;

    // willReadFrequently: checkScratchProgress() polls getImageData repeatedly.
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    const rect = this.scratchWrap.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    ctx.scale(dpr, dpr);

    // Premium brushed-gold foil, drawn once. Warm champagne-to-deep-gold tones
    // matched to the CSS material (.email-capture__foil) so the canvas and its
    // overlays read as a single surface. The white/black specular overlays ride
    // on top of this base and read as gold highlights. Several gradients + fine
    // vertical brushing; no per-frame work (composites once, before scratching).
    const w = rect.width;
    const h = rect.height;

    // Base body: top catches light (bright champagne), bottom falls into deeper gold.
    const base = ctx.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, '#f7e8b4');
    base.addColorStop(0.5, '#e2c469');
    base.addColorStop(1, '#c2941f');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    // Diagonal cross-light for an anodised sheen.
    const cross = ctx.createLinearGradient(0, 0, w, h);
    cross.addColorStop(0, 'rgba(255,255,255,0.18)');
    cross.addColorStop(0.45, 'rgba(255,255,255,0)');
    cross.addColorStop(0.7, 'rgba(0,0,0,0.05)');
    cross.addColorStop(1, 'rgba(255,255,255,0.12)');
    ctx.fillStyle = cross;
    ctx.fillRect(0, 0, w, h);

    // Soft off-centre highlight lobe (a single studio key light).
    const lobe = ctx.createRadialGradient(
      w * 0.32, h * 0.1, 0,
      w * 0.32, h * 0.1, Math.max(w, h) * 0.9
    );
    lobe.addColorStop(0, 'rgba(255,255,255,0.22)');
    lobe.addColorStop(0.5, 'rgba(255,255,255,0)');
    ctx.fillStyle = lobe;
    ctx.fillRect(0, 0, w, h);

    // Fine vertical brushing — thin alternating light/dark lines at low alpha.
    // Subtle enough that it never reads as visible stripes.
    for (let x = 0; x < w; x += 2) {
      ctx.fillStyle = x % 4 === 0 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.03)';
      ctx.fillRect(x, 0, 1, h);
    }

    // The "scratch here" label is a live DOM element engraved into the foil with
    // CSS (.email-capture__scratch-label) — not baked into the canvas — so it can
    // carry the engraved shading + progressive text shine. It fades with the rest
    // of the material on the first scratch (.is-touched), rather than being erased.

    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = 42;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    this.scratchCtx = ctx;
    this.scratchRect = rect;
    this.scratchThreshold = this.num('scratch_threshold') / 100;
    this.lastPoint = null;
    this.checkScheduled = false;

    this.bindScratch();
  }

  bindScratch() {
    const canvas = this.canvas;

    const point = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const start = (e) => {
      if (this.scratchCompleted) return;
      this.isScratching = true;
      this.lastPoint = point(e);
      this.scratchAt(this.lastPoint);
      if (!this.scratchStarted) {
        this.scratchStarted = true;
        // Fade the idle foil material out so it never bleeds onto the revealed
        // print, and stop interactive lighting (hotspot + tilt ease to neutral).
        if (this.scratchWrap) this.scratchWrap.classList.add('is-touched');
        this.disableMaterial();
        this.track('Email Capture Scratch Started');
      }
      if (e.pointerId != null && canvas.setPointerCapture) {
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
    };

    const move = (e) => {
      if (!this.isScratching || this.scratchCompleted) return;
      e.preventDefault();
      const p = point(e);
      const ctx = this.scratchCtx;
      ctx.beginPath();
      ctx.moveTo(this.lastPoint.x, this.lastPoint.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      this.lastPoint = p;
      this.scheduleCheck();
    };

    const end = () => {
      if (!this.isScratching) return;
      this.isScratching = false;
      this.scheduleCheck();
    };

    canvas.addEventListener('pointerdown', start);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointerleave', end);
    canvas.addEventListener('pointercancel', end);
  }

  scratchAt(p) {
    const ctx = this.scratchCtx;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 21, 0, Math.PI * 2);
    ctx.fill();
  }

  scheduleCheck() {
    if (this.checkScheduled) return;
    this.checkScheduled = true;
    window.setTimeout(() => {
      this.checkScheduled = false;
      this.checkScratchProgress();
    }, 120);
  }

  checkScratchProgress() {
    if (this.scratchCompleted || !this.scratchCtx) return;

    const { width, height } = this.canvas;
    const step = 32; // sample sparsely for performance
    let transparent = 0;
    let total = 0;
    try {
      const data = this.scratchCtx.getImageData(0, 0, width, height).data;
      for (let i = 3; i < data.length; i += 4 * step) {
        total++;
        if (data[i] < 128) transparent++;
      }
    } catch (e) {
      return; // e.g. tainted canvas — bail out safely
    }

    if (total && transparent / total >= this.scratchThreshold) {
      this.completeScratch();
    }
  }

  completeScratch() {
    if (this.scratchCompleted) return;
    this.scratchCompleted = true;

    // Smoothly fade the remaining foil away (ease-in-out via CSS .is-revealed)
    // and collapse the "scratch below…" prompt.
    if (this.scratchWrap) this.scratchWrap.classList.add('is-revealed');
    this.setAttribute('data-revealing', 'true');
    this.track('Email Capture Scratch Completed');

    const target = this.headingEl && this.headingEl.dataset.revealHeading;

    const revealForm = () => {
      this.setAttribute('data-revealed', 'true');
      this.startCountdown();
      const email = this.querySelector('.js-email-capture-email');
      if (email) email.focus();
      // The CSS trims the stack so the button clears the fold on a typical phone, but a
      // small viewport, a long heading or a large text-size setting can still push it
      // under. Wait for the reveal transition (max-height 0.55s) to finish expanding the
      // form, then bring it into view if any part of it is still below the dialog.
      window.setTimeout(() => this.ensureFormVisible(), 600);
    };

    // Reduced motion: swap the heading text and reveal instantly.
    if (this.prefersReducedMotion()) {
      if (target) this.headingEl.textContent = target;
      revealForm();
      return;
    }

    // Choreography: 1) card finishes fading → 2) heading crossfades to the
    // reveal copy → 3) form fades up beneath.
    this.revealTimer = window.setTimeout(() => {
      if (this.headingEl && target) {
        this.swapHeading(this.headingEl, target, revealForm);
      } else {
        revealForm();
      }
    }, 600);
  }

  prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Fade the current heading out, swap in the reveal copy, fade it back in.
  // FADE_MS must match the opacity transition on .email-capture__heading.
  swapHeading(el, target, onComplete) {
    const FADE_MS = 260;

    el.classList.add('is-swapping');
    this.twTimer = window.setTimeout(() => {
      el.textContent = target;
      el.classList.remove('is-swapping');
      this.twTimer = window.setTimeout(() => {
        if (onComplete) onComplete();
      }, FADE_MS);
    }, FADE_MS);
  }

  /* ---------- interactive foil material ---------- */

  // Interactive lighting for the unscratched foil: a pointer-following highlight
  // (CSS custom properties --light-x / --light-y, consumed by the hotspot layer
  // .email-capture__foil) plus a subtle card tilt (rotateX/rotateY on
  // .email-capture__scratch). Both ease toward their targets inside a single
  // requestAnimationFrame loop and only ever touch transform + custom properties,
  // never layout. Purely additive — scratch, reveal, countdown and analytics all
  // behave identically with this off. No-op under reduced motion.
  setupMaterial() {
    this.materialEl = this.scratchWrap; // .email-capture__scratch
    if (!this.materialEl || this.prefersReducedMotion()) return;

    this.materialActive = true;
    this.tilt = { x: 0, y: 0 };
    this.tiltTarget = { x: 0, y: 0 };
    this.light = { x: 50, y: 38 };
    this.lightTarget = { x: 50, y: 38 };

    this.onMaterialMove = (e) => {
      if (!this.materialActive) return;
      const r = this.materialEl.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const px = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const py = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
      this.lightTarget.x = px * 100;
      this.lightTarget.y = py * 100;
      // Tilt toward the pointer, capped at ±3° per axis.
      this.tiltTarget.x = (0.5 - py) * 6;
      this.tiltTarget.y = (px - 0.5) * 6;
      this.requestMaterialFrame();
    };

    this.onMaterialLeave = () => {
      // Ease back to a neutral, centred resting state.
      this.tiltTarget.x = 0;
      this.tiltTarget.y = 0;
      this.lightTarget.x = 50;
      this.lightTarget.y = 38;
      this.requestMaterialFrame();
    };

    this.materialEl.addEventListener('pointermove', this.onMaterialMove);
    this.materialEl.addEventListener('pointerenter', this.onMaterialMove);
    this.materialEl.addEventListener('pointerleave', this.onMaterialLeave);
  }

  // Ease current tilt + light toward their targets, one rAF at a time. Self-
  // terminating once everything has settled.
  requestMaterialFrame() {
    if (this.materialRaf || !this.materialEl) return;
    const EASE = 0.14;
    const tick = () => {
      this.materialRaf = null;
      this.tilt.x += (this.tiltTarget.x - this.tilt.x) * EASE;
      this.tilt.y += (this.tiltTarget.y - this.tilt.y) * EASE;
      this.light.x += (this.lightTarget.x - this.light.x) * EASE;
      this.light.y += (this.lightTarget.y - this.light.y) * EASE;

      this.materialEl.style.transform =
        'rotateX(' + this.tilt.x.toFixed(2) + 'deg) rotateY(' + this.tilt.y.toFixed(2) + 'deg)';
      this.materialEl.style.setProperty('--light-x', this.light.x.toFixed(1) + '%');
      this.materialEl.style.setProperty('--light-y', this.light.y.toFixed(1) + '%');

      const settled =
        Math.abs(this.tiltTarget.x - this.tilt.x) < 0.02 &&
        Math.abs(this.tiltTarget.y - this.tilt.y) < 0.02 &&
        Math.abs(this.lightTarget.x - this.light.x) < 0.1 &&
        Math.abs(this.lightTarget.y - this.light.y) < 0.1;

      if (settled) {
        this.tilt.x = this.tiltTarget.x;
        this.tilt.y = this.tiltTarget.y;
        // Fully neutral → drop the transform so nothing lingers on the card.
        if (this.tilt.x === 0 && this.tilt.y === 0) {
          this.materialEl.style.transform = '';
        }
        return;
      }
      this.materialRaf = requestAnimationFrame(tick);
    };
    this.materialRaf = requestAnimationFrame(tick);
  }

  // First scratch: stop reacting to the pointer and ease the tilt back to flat.
  // The CSS (.is-touched) fades the foil / sheen / sparkles out.
  disableMaterial() {
    if (!this.materialActive) return;
    this.materialActive = false;
    if (this.tiltTarget) {
      this.tiltTarget.x = 0;
      this.tiltTarget.y = 0;
      this.requestMaterialFrame();
    }
  }

  // Remove pointer listeners + cancel any pending frame (teardown).
  teardownMaterial() {
    this.materialActive = false;
    if (this.materialRaf) {
      window.cancelAnimationFrame(this.materialRaf);
      this.materialRaf = null;
    }
    if (this.materialEl) {
      if (this.onMaterialMove) {
        this.materialEl.removeEventListener('pointermove', this.onMaterialMove);
        this.materialEl.removeEventListener('pointerenter', this.onMaterialMove);
      }
      if (this.onMaterialLeave) {
        this.materialEl.removeEventListener('pointerleave', this.onMaterialLeave);
      }
    }
  }

  /* ---------- countdown ---------- */

  startCountdown() {
    if (this.countdownTimer) return;
    this.countdownEnd = Date.now() + this.num('countdown_seconds') * 1000;
    this.renderCountdown();
    this.countdownTimer = window.setInterval(() => this.renderCountdown(), 250);
  }

  renderCountdown() {
    const remaining = Math.max(0, Math.round((this.countdownEnd - Date.now()) / 1000));
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    if (this.countdownEl) {
      this.countdownEl.textContent = m + ':' + (s < 10 ? '0' + s : s);
    }
    if (remaining <= 0) {
      this.stopCountdown();
      // The discount does not expire — the code stays valid and the offer stays on
      // screen, frozen at 0:00. The timer is urgency framing, not an enforcement
      // mechanism, so nothing is withdrawn when it runs out.
      this.track('Email Capture Expired', {
        had_email: !!(this.emailInput && this.emailInput.value.trim()),
      });
    }
  }

  stopCountdown() {
    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /* ---------- submit (customer creation) ---------- */

  onSubmit(e) {
    // Pass-through for the real submit we trigger below: let the browser +
    // Shopify's hCaptcha handle it natively — don't intercept.
    if (this.nativeSubmit) return;

    e.preventDefault();

    // In the theme editor, don't create real customers — just preview success.
    if (this.designMode) {
      this.onSuccess();
      return;
    }

    if (this.submitting) return;
    this.submitting = true;

    const emailKey = this.emailInput ? this.emailInput.value.toLowerCase().trim() : '';

    // Identify BEFORE the native submit navigates away, so a drop-off between
    // "Email Submitted" and "Success" (e.g. a captcha challenge, redirect
    // failure) still leaves the person tied to their email in Mixpanel.
    this.identifyWithEmail(emailKey);
    // No raw email as an event property: identifyWithEmail() above already sets the
    // distinct_id and the $email profile property, so carrying it on every event is
    // redundant PII spread across the event stream.
    this.track('Email Capture Email Submitted', { has_email: !!emailKey });

    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');
    }

    // Stamp segmentation onto the customer record before the form goes. Wrapped because
    // decorating tags is a nice-to-have: it must never be the reason a signup fails, so any
    // problem here leaves the merchant's original contact_tags value in place and carries on.
    try {
      this.buildTags();
    } catch (err) {
      this.log('buildTags failed, submitting with the original tags', err);
    }

    this.log('submitting', {
      tags: this.form.querySelector('[name="contact[tags]"]')
        ? this.form.querySelector('[name="contact[tags]"]').value
        : '(no tags input)',
      action: this.form.getAttribute('action'),
      // If this is false the POST goes out with no captcha token, which /contact rejects
      // with a 400 on this store even when spam protection is switched "off" in Admin.
      captcha_available: !!(
        window.Shopify &&
        window.Shopify.captcha &&
        typeof window.Shopify.captcha.protect === 'function'
      ),
    });

    // Submit natively so Shopify's captcha — governed entirely by the store's
    // spam-protection preference — runs and attaches a token. This navigates;
    // the success stage is restored on return via shouldRestoreSuccess (the
    // form action carries #email-capture-success).
    this.submitNative();
  }

  /* ---------- customer tags ---------- */

  // Rewrite the ONE hidden contact[tags] input rather than adding a second one: duplicate
  // params don't merge, one silently wins, and there's no error to notice.
  //
  // Built from defaultValue (the merchant's `contact_tags` setting as rendered) so calling
  // this more than once can't compound. Commas are the tag separator, so every part is
  // stripped of them — an unsanitised value would silently become two tags.
  //
  // Everything is `ec-` prefixed so these sort together in Admin's tag picker instead of
  // scattering through it. Four dimensions is about the ceiling before the tag list becomes
  // combinatorial. These are client-composed and therefore spoofable: segmentation hints,
  // not a trust boundary.
  buildTags() {
    if (!this.form) return;
    const input = this.form.querySelector('[name="contact[tags]"]');
    if (!input) return;

    const parts = String(input.defaultValue || '')
      .split(',')
      .concat([
        'ec-variant-' + this.variant,
        'ec-page-' + this.cfg('page_type', 'unknown'),
        'ec-market-' + this.cfg('country', 'unknown'),
      ]);

    const seen = {};
    const tags = [];
    parts.forEach((part) => {
      const tag = String(part == null ? '' : part)
        .replace(/,/g, ' ')
        .trim()
        .toLowerCase();
      if (!tag || seen[tag]) return;
      seen[tag] = true;
      tags.push(tag);
    });

    input.value = tags.join(', ');
  }

  submitNative() {
    this.nativeSubmit = true;

    // Keep the button in its loading state — the page is about to reload.
    const submit = () => {
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    };

    // Route through Shopify's supported captcha wireup so the submission carries
    // a valid token. When spam protection is enabled this is usually an
    // invisible (challenge-free) token; when it's disabled, protect() is absent
    // and we submit plainly. Either way the store preference decides behavior.
    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && typeof captcha.protect === 'function') {
      captcha.protect(this.form, submit);
    } else {
      submit();
    }
  }

  onSuccess() {
    if (!this.designMode) {
      try {
        window.localStorage.setItem(this.STORAGE_CLAIMED, '1');
      } catch (e) {
        /* ignore */
      }
    }
    this.track('Email Capture Success');
    this.applyDiscountInBackground();
    this.close();
    this.showConfirmationBar();
  }

  /* ---------- copy code ---------- */

  // Scroll the claim button into view if the reveal left it below the dialog's viewport.
  // No-ops when it already fits, so it costs one rect read and nothing else on desktop.
  ensureFormVisible() {
    if (!this.submitBtn || !this.dialog) return;
    const btn = this.submitBtn.getBoundingClientRect();
    const box = this.dialog.getBoundingClientRect();
    if (btn.bottom <= box.bottom - 8) return;

    this.dialog.scrollTo({
      top: this.dialog.scrollTop + (btn.bottom - box.bottom) + 24,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /* ---------- confirmation bar ---------- */

  // Replaces the old success panel with a notification bar in the announcement strip:
  // it slides open beneath the existing "Worldwide shipping / Certificate of Authenticity"
  // banner and above the header, shows the confirmation, then closes itself.
  //
  // Built in JS with inline styles on purpose. The popup's stylesheet is compiled by legacy
  // Sass, where one bad declaration 422s the asset and takes the popup's whole appearance
  // with it, so a change that needs no CSS is worth having.
  //
  // Height and typography are read off the live announcement bar rather than hardcoded, so
  // this matches whatever the merchant sets in the header section at both breakpoints
  // (35px desktop / 26px mobile) without duplicating those numbers.
  //
  // Colour is deliberately NOT inherited: the bar is white with dark text so it reads as a
  // distinct confirmation rather than a second announcement line, and so it stays legible
  // whatever the merchant sets the announcement bar to.
  showConfirmationBar() {
    const message = this.dataset.toastText || 'Discount applied to your cart';
    const code = this.dataset.discountCode || '';
    const copiedLabel = this.dataset.copiedLabel || 'Copied';

    if (this.barEl) this.dismissConfirmationBar(true);

    const announcement = document.querySelector('.header__announcement');
    // Cover the announcement bar where there is one, otherwise pin to the top of the
    // viewport. Nothing is inserted into the flow either way — see the overlay note below.
    // With no header at all there is nowhere sensible to put this.
    if (!announcement && !document.querySelector('.header')) return;

    const background = 'var(--background-white)';
    const foreground = 'var(--text-black)';
    let height = window.innerWidth <= 991 ? 26 : 35;
    if (announcement) {
      const measured = Math.round(announcement.getBoundingClientRect().height);
      if (measured > 0) height = measured;
    }

    const reduce = this.prefersReducedMotion();

    // Out of flow, deliberately. This used to be inserted between .header__announcement and
    // <header> — both position: relative — with its height animated from 0, which pushed the
    // header and the whole page down 35px. That is a layout shift of roughly 35/viewport on
    // its own, and nothing excuses it: the bar appears on a fresh page load after the
    // /contact redirect, so there is no recent input on that document for CLS to discount it.
    //
    // It now slides over the announcement bar rather than above it: absolutely positioned
    // inside .header__announcement, which is already position: relative with overflow: hidden,
    // so the slide is clipped to exactly the strip it is replacing. With no announcement bar
    // to sit in it falls back to a fixed overlay at the top of the viewport. Either way
    // nothing in the document moves.
    const overlayInAnnouncement = !!announcement;

    const bar = document.createElement('div');
    bar.className = 'email-capture-bar';
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-live', 'polite');
    bar.style.cssText = [
      overlayInAnnouncement ? 'position:absolute' : 'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      overlayInAnnouncement ? 'bottom:0' : '',
      overlayInAnnouncement ? '' : 'z-index:98',
      'overflow:hidden',
      'background:' + background,
      'color:' + foreground,
      'transform:translateY(-100%)',
      reduce ? '' : 'transition:transform .38s cubic-bezier(.22,.61,.36,1)',
    ]
      .filter(Boolean)
      .join(';');

    // Fixed-height inner so the strip matches the announcement bar it covers exactly,
    // rather than taking its height from the copy.
    const inner = document.createElement('div');
    inner.className = 'text-cta';
    inner.style.cssText = [
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'gap:10px',
      'height:' + height + 'px',
      'padding:0 16px',
      'white-space:nowrap',
      'opacity:0',
      reduce ? '' : 'transition:opacity .26s ease',
    ]
      .filter(Boolean)
      .join(';');

    const label = document.createElement('span');
    label.textContent = message;
    inner.appendChild(label);

    if (code) {
      const sep = document.createElement('span');
      sep.textContent = '·';
      sep.setAttribute('aria-hidden', 'true');
      sep.style.opacity = '0.5';
      inner.appendChild(sep);

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.setAttribute('aria-label', 'Copy discount code ' + code);
      chip.textContent = code;
      // Reads as part of the banner line, not a button bolted into it.
      chip.style.cssText = [
        'padding:0',
        'background:none',
        'border:0',
        'border-bottom:1px dashed currentColor',
        'border-radius:0',
        'color:inherit',
        'font:inherit',
        'letter-spacing:inherit',
        'text-transform:inherit',
        'cursor:pointer',
      ].join(';');
      chip.addEventListener('click', async () => {
        const ok = await this.copyCode(code);
        if (!ok) return;
        chip.textContent = copiedLabel;
        chip.style.borderBottomStyle = 'solid';
        // They're interacting — don't close it out from under them.
        this.restartBarTimer();
        window.setTimeout(() => {
          chip.textContent = code;
          chip.style.borderBottomStyle = 'dashed';
        }, 1600);
      });
      inner.appendChild(chip);
    }

    // Bottom edge against the header below. currentColor at half a pixel was right when the
    // bar was dark-on-dark; on a white ground it reads as a hard black line, so use the
    // theme's light border token at a full pixel instead.
    const rule = document.createElement('span');
    rule.setAttribute('aria-hidden', 'true');
    rule.style.cssText = [
      'position:absolute',
      'left:0',
      'right:0',
      'bottom:0',
      'height:1px',
      'background:var(--border-light)',
      'pointer-events:none',
    ].join(';');

    bar.appendChild(inner);
    bar.appendChild(rule);
    if (overlayInAnnouncement) {
      announcement.appendChild(bar);
    } else {
      document.body.appendChild(bar);
    }

    this.barEl = bar;
    this.barInner = inner;
    this.barHeight = height;

    if (reduce) {
      bar.style.transform = 'translateY(0)';
      inner.style.opacity = '1';
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bar.style.transform = 'translateY(0)';
          // Fade the copy in once the strip has arrived, so it doesn't read mid-slide.
          window.setTimeout(() => {
            inner.style.opacity = '1';
          }, 260);
        });
      });
    }

    this.restartBarTimer();
  }

  // Long enough to read a code and decide to copy it.
  restartBarTimer() {
    window.clearTimeout(this.barTimer);
    this.barTimer = window.setTimeout(() => this.dismissConfirmationBar(), BAR_DWELL_MS);
  }

  // Reverse of the entrance: the copy fades out first, then the strip closes to nothing.
  dismissConfirmationBar(immediate) {
    const bar = this.barEl;
    const inner = this.barInner;
    if (!bar) return;
    this.barEl = null;
    this.barInner = null;
    window.clearTimeout(this.barTimer);

    const remove = () => {
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    };

    if (immediate || this.prefersReducedMotion()) {
      remove();
      return;
    }

    if (inner) inner.style.opacity = '0';
    window.setTimeout(() => {
      bar.style.transform = 'translateY(-100%)';
      window.setTimeout(remove, 420);
    }, 240);
  }

  // Write `code` to the clipboard, resolving true/false. Used by the bar's code chip.
  async copyCode(code) {
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch (e) {
      ok = this.legacyCopy(code);
    }
    if (ok) this.track('Email Capture Code Copied');
    return ok;
  }

  legacyCopy(text) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  }

  /* ---------- analytics ---------- */

  track(event, props) {
    if (this.designMode) return; // don't pollute analytics from the editor
    if (!(window.mixpanel && typeof window.mixpanel.track === 'function')) return;
    // window.__rumContext (layout/theme.liquid) already carries template, template_suffix,
    // country, language, currency, theme_name and theme_id. Merging it means every event
    // here is sliceable by market and template without duplicating that plumbing. Read it
    // lazily rather than caching in the constructor so there's no load-order assumption,
    // and put `variant` after it so a future `variant` key in rum context can't clobber ours.
    window.mixpanel.track(
      event,
      Object.assign({}, window.__rumContext || {}, { variant: this.variant }, props || {})
    );
  }

  // Progressive identity upgrade (anonymous -> email). Mirrors the checkout
  // pixel's identifyWithEmail() so the popup and checkout resolve to the same
  // distinct_id (lowercased email) and merge server-side: alias stitches the
  // prior anonymous funnel events to the email, and the localStorage guard
  // prevents re-aliasing the same person on repeat visits. identify() persists
  // the distinct_id synchronously, so it survives the native-submit navigation
  // and the Success event on return stays attributed to this person.
  //
  // NOTE: `emailKey` must already be lowercased + trimmed by the caller, so the
  // guard key and distinct_id match the checkout pixel exactly.
  identifyWithEmail(emailKey) {
    if (this.designMode || !emailKey) return;
    if (!(window.mixpanel && typeof window.mixpanel.identify === 'function')) return;
    try {
      const aliasKey = 'mp_' + emailKey;
      let aliased = false;
      try {
        aliased = !!window.localStorage.getItem(aliasKey);
      } catch (e) {
        /* storage blocked — skip the guard, alias anyway */
      }

      if (!aliased) {
        window.mixpanel.alias(emailKey);
        try {
          window.localStorage.setItem(aliasKey, 'true');
        } catch (e) {
          /* ignore */
        }
      }

      window.mixpanel.identify(emailKey);
      if (window.mixpanel.people && typeof window.mixpanel.people.set === 'function') {
        window.mixpanel.people.set({ $email: emailKey });
      }
    } catch (e) {
      /* ignore */
    }
  }
}

if (!customElements.get('email-popup')) {
  customElements.define('email-popup', EmailPopup);
}
